import { Space } from 'tradera/components/atoms/space/space';
import { Text } from 'tradera/components/atoms/text/text';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import {
    useLocalizedPriceFormatter,
    useLocalizedPriceFormatterNoSymbol
} from 'tradera/localization/use-localized-price-formatter';
import NoImgIcon from '@tradera/blueprint/theme/icons2/no-image.svg?jsx';
import { classNames } from 'tradera/utils/class-names';
import { Highlighter } from 'tradera/apps/my-tradera/sold/components/highlighter';
import { TextSplit } from 'tradera/components/atoms/text-split/text-split';
import { MaybeLink } from 'tradera/components/maybe-link/maybe-link';
import { ItemType } from 'tradera/apps/my-tradera/listing-item/item-type/item-type';
import { type PublicMember } from 'tradera/state/services/types/webapi-mytradera-generated';
import EyeIcon from 'tradera/blueprint/theme/icons2/eye.svg?jsx';
import HeartIcon from 'tradera/blueprint/theme/icons2/heart.svg?jsx';
import { isNullOrUndefined } from 'tradera/utils/condition-check';
import { WishListButton } from 'tradera/components/wish-list-button/wish-list-button';

import styles from './listing-item.module.scss';
import Alink from 'tradera/components/alink/alink';
import { CurrencyRateType } from 'tradera/state/multi-currency/reducer';
import { useAppSelector } from 'tradera/state/hooks';
import { selectMemberId } from 'tradera/state/member/selectors';
import { EndTimeLabel } from 'tradera/components/end-time-label/end-time-label';
import { isMobile } from 'react-device-detect';

type ListingItemWithActionsProps = {
    shortDescription: string;
    itemId?: number;
    imageTemplate?: string;
    itemUrl?: string;
    itemPrice?: number;
    isActive?: boolean;
    shippingCost?: number;
    reservePrice?: number;
    endDate?: string;
    showDateOnEnded?: boolean;
    ownReference?: string;
    existsInWishlist?: boolean;
    itemType?: string;
    bidCount?: number;
    grayedOut?: boolean;
    highlight?: string;
    onShowBidHistory?: (itemId: number) => void;
    onWishlistClick?: (itemId: number) => void;
    actions?: React.ReactNode;
    statusBadge?: React.ReactNode;
    isTakeawayOnly?: boolean;
    leadingBidder?: PublicMember | null;
    maxBid?: number | null;
    viewsCount?: number | null;
    wishlistCount?: number | null;
    sellerAlias?: string | null;
    sellerId?: number | null;
};

export const ListingItemWithActions = ({
    shortDescription,
    itemId,
    imageTemplate,
    itemUrl,
    itemPrice,
    isActive,
    reservePrice,
    shippingCost,
    endDate,
    showDateOnEnded,
    ownReference,
    itemType,
    bidCount,
    grayedOut,
    highlight,
    onShowBidHistory,
    onWishlistClick,
    existsInWishlist,
    actions,
    statusBadge,
    isTakeawayOnly,
    leadingBidder,
    maxBid,
    viewsCount,
    wishlistCount,
    sellerAlias,
    sellerId
}: ListingItemWithActionsProps) => {
    const { t } = useTranslator();
    const memberId = useAppSelector(selectMemberId);
    const isBuyer = memberId !== sellerId;
    const formatPrice = useLocalizedPriceFormatter(
        isBuyer ? CurrencyRateType.Buyer : CurrencyRateType.Seller
    );
    const formatPriceNoSymbol = useLocalizedPriceFormatterNoSymbol(
        isBuyer ? CurrencyRateType.Buyer : CurrencyRateType.Seller
    );
    const { toLocalizedUrl } = useUrlLocalizer();

    const hasReservePrice = reservePrice && reservePrice > 0;
    const showSellerAlias = sellerAlias && sellerId;

    return (
        <Space direction="vertical" gap={2} className="w-100">
            <Space gap={2} align="start">
                <div className="position-relative">
                    <MaybeLink href={itemUrl}>
                        {imageTemplate ? (
                            <img
                                className={classNames(
                                    'flex-shrink-0',
                                    styles.listingImageWithActions,
                                    {
                                        'opacity-50': grayedOut
                                    }
                                )}
                                src={imageTemplate?.replace(
                                    '{format}',
                                    'small-fit'
                                )}
                                alt={shortDescription}
                                loading="lazy"
                            />
                        ) : (
                            <span
                                className={classNames(
                                    'bg-gray-100 d-flex justify-content-center align-items-center',
                                    styles.listingImage,
                                    {
                                        'opacity-50': grayedOut
                                    }
                                )}>
                                <NoImgIcon className="icon icon-lg text-gray-300" />
                            </span>
                        )}
                        {statusBadge}
                    </MaybeLink>
                    {itemId && onWishlistClick && (
                        <WishListButton
                            toggled={existsInWishlist}
                            className={styles.wishlistButton}
                            onClick={() => onWishlistClick(itemId)}
                        />
                    )}
                </div>
                <Space
                    direction="vertical"
                    grow
                    className={styles.minWidthZero}>
                    <Space
                        justify="between"
                        className={classNames({
                            'opacity-50': grayedOut
                        })}>
                        {showSellerAlias ? (
                            <Alink
                                className="text-inter-medium text-gray-600 size-oslo"
                                href={toLocalizedUrl(
                                    `/profile/items/${sellerId}/${sellerAlias}`
                                )}>
                                {sellerAlias}
                            </Alink>
                        ) : (
                            <Text
                                size="oslo"
                                color="gray-600"
                                fontWeight="medium">
                                {t('myt_columns_objectNo')}{' '}
                                <Highlighter
                                    highlightAllWhenMatching={itemId}
                                    highlight={highlight}>
                                    <TextSplit>{itemId}</TextSplit>
                                </Highlighter>
                            </Text>
                        )}
                        {typeof itemPrice !== 'undefined' ? (
                            <Text
                                fontWeight="heavy"
                                className="@d-none @d-md-block">
                                {formatPrice(itemPrice)}
                            </Text>
                        ) : null}
                    </Space>
                    <Space
                        justify="between"
                        className={classNames({
                            'opacity-50': grayedOut
                        })}>
                        <Text fontWeight="medium" className="text-truncate">
                            <MaybeLink href={itemUrl}>
                                <Highlighter highlight={highlight}>
                                    {shortDescription}
                                </Highlighter>
                            </MaybeLink>
                        </Text>
                        {shippingCost ? (
                            <Text
                                className="@d-none @d-md-block"
                                nowrap
                                color="gray-600">
                                {t('myt_shippingAmountCost', {
                                    cost: formatPrice(shippingCost)
                                })}
                            </Text>
                        ) : isTakeawayOnly ? (
                            <Text
                                className="@d-none @d-md-block"
                                color="gray-600">
                                {t('myt_takeawayOnly')}
                            </Text>
                        ) : null}
                    </Space>
                    <Space
                        justify="between"
                        className={classNames({
                            'opacity-50': grayedOut
                        })}>
                        <Text
                            color="gray-600"
                            className={isMobile ? '' : 'text-truncate'}>
                            <EndTimeLabel
                                endDate={endDate}
                                isActive={isActive}
                                showDateOnEnded={showDateOnEnded}
                            />
                            {hasReservePrice && (
                                <span className="@d-none @d-md-inline">
                                    &ensp;|&ensp;
                                    {t('myt_columns_reservePrice')}{' '}
                                    {formatPrice(reservePrice)}
                                </span>
                            )}
                            {ownReference && (
                                <span className="@d-none @d-md-inline">
                                    &ensp;|&ensp;
                                    {t('myt_columns_ownReference')}{' '}
                                    <Highlighter highlight={highlight}>
                                        {ownReference}
                                    </Highlighter>
                                </span>
                            )}
                            {maxBid && (
                                <span className="@d-none @d-md-inline">
                                    &ensp;|&ensp;
                                    {t('myt_buyer_autobid')}{' '}
                                    <Highlighter highlight={highlight}>
                                        {formatPrice(maxBid)}
                                    </Highlighter>
                                </span>
                            )}
                        </Text>
                        {typeof itemPrice !== 'undefined' ? (
                            <Text
                                fontWeight="heavy"
                                nowrap
                                className="@d-inline @d-md-none">
                                {maxBid && (
                                    <span className="text-gray-600 font-light">
                                        ({formatPriceNoSymbol(maxBid)}){' '}
                                    </span>
                                )}
                                {formatPrice(itemPrice)}
                            </Text>
                        ) : null}
                    </Space>
                    {hasReservePrice || ownReference ? (
                        <Space
                            direction="vertical"
                            className={classNames('@d-md-none', {
                                'opacity-50': grayedOut
                            })}>
                            {hasReservePrice && (
                                <span className="text-truncate">
                                    {t('myt_columns_reservePrice')}{' '}
                                    {formatPrice(reservePrice)}
                                </span>
                            )}
                            {ownReference && (
                                <span className="text-truncate">
                                    {t('myt_columns_ownReference')}{' '}
                                    {ownReference}
                                </span>
                            )}
                        </Space>
                    ) : null}
                    <Space justify="between">
                        <div
                            className={classNames(
                                'd-flex gap-2 text-gray-600',
                                {
                                    'opacity-50': grayedOut
                                }
                            )}>
                            <ItemType
                                itemType={itemType}
                                bidCount={bidCount}
                                itemId={itemId}
                                onShowBidHistory={onShowBidHistory}
                                leadingBidder={leadingBidder}
                            />
                            {!isNullOrUndefined(viewsCount) && (
                                <span className="@d-none @d-md-flex gap-2">
                                    |
                                    <ViewsIcon count={viewsCount} />
                                </span>
                            )}
                            {!isNullOrUndefined(wishlistCount) && (
                                <span className="@d-none @d-md-flex gap-2">
                                    |
                                    <WishlistIcon count={wishlistCount} />
                                </span>
                            )}
                        </div>
                        <div className="@d-none @d-md-flex">
                            <Space
                                gap={2}
                                direction="horizontal-reverse"
                                className="mt-n2">
                                {actions}
                            </Space>
                        </div>
                        <div className="@d-block @d-md-none">
                            {shippingCost ? (
                                <Text color="gray-600">
                                    {t('myt_shippingAmountCost', {
                                        cost: formatPrice(shippingCost)
                                    })}
                                </Text>
                            ) : isTakeawayOnly ? (
                                <Text color="gray-600">
                                    {t('myt_takeawayOnly')}
                                </Text>
                            ) : null}
                        </div>
                    </Space>
                </Space>
            </Space>
            <div className="@d-flex @d-md-none flex-column gap-2">
                <Space gap={2} direction="horizontal">
                    {actions}
                </Space>
                {(!isNullOrUndefined(viewsCount) ||
                    !isNullOrUndefined(wishlistCount)) && (
                    <div className="d-flex gap-2 text-gray-600 size-oslo">
                        {!isNullOrUndefined(viewsCount) && (
                            <ViewsIcon count={viewsCount} />
                        )}
                        {!isNullOrUndefined(viewsCount) &&
                            !isNullOrUndefined(wishlistCount) && <span>|</span>}
                        {!isNullOrUndefined(wishlistCount) && (
                            <WishlistIcon count={wishlistCount} />
                        )}
                    </div>
                )}
            </div>
        </Space>
    );
};

type IconProps = {
    count: number;
};

const ViewsIcon = ({ count }: IconProps) => {
    const { t } = useTranslator();
    return (
        <span className="d-flex align-items-center">
            <EyeIcon
                aria-label={t('myt_listings_viewsCount')}
                className="icon icon-sm mr-1">
                <title>{t('myt_listings_viewsCount')}</title>
            </EyeIcon>
            {count}
        </span>
    );
};

const WishlistIcon = ({ count }: IconProps) => {
    const { t } = useTranslator();
    return (
        <span className="d-flex align-items-center">
            <HeartIcon
                aria-label={t('myt_listings_wishlistCount')}
                className="icon icon-sm mr-1">
                <title>{t('myt_listings_wishlistCount')}</title>
            </HeartIcon>
            {count}
        </span>
    );
};
